import { useEffect, useRef, useState } from 'react'
import { useTranslations } from 'use-intl'
import { AnimatePresence, motion, useInView } from 'framer-motion'
import { Link, useParams } from '@tanstack/react-router'
import { ArrowRight, Check, X, type LucideIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { HighlightText } from '@/components/ui/highlight-text'
import { RevealGroup, RevealItem } from '@/components/ui/reveal'
import { COMPARE_CARDS, type CompareTab } from '../data/compare'

const FEATURE_ROUTE = {
  people: '/$locale/solutions/people-security/$feature',
  property: '/$locale/solutions/property-security/$feature',
} as const

// Gentle fade + slight glide when the bento swaps. Keep x small so the cards
// never crop against the overflow-hidden edges mid-animation.
const contentVariants = {
  enter: { opacity: 0, x: 6 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -6 },
}

const springPill = { type: 'spring', stiffness: 380, damping: 32 } as const

/** One card. Reads its copy from `home.compare.cards.<key>.<tab>`. */
function CompareCard({
  card,
  tab,
}: {
  card: (typeof COMPARE_CARDS)[number]
  tab: CompareTab
}) {
  const t = useTranslations('home.compare.cards')
  const base = `${card.key}.${tab}`
  const isWith = tab === 'with'

  const { locale } = useParams({ from: '/$locale' })

  return (
    <Link
      to={FEATURE_ROUTE[card.link.category] as never}
      params={{ locale, feature: card.link.feature } as never}
      className={cn(
        'relative flex flex-col overflow-hidden rounded-3xl px-4 py-6 no-underline lg:p-5',
        // Cards are links: pointer + a very light lift on hover. The raised
        // z-index keeps the lifted card above its neighbours and the band's
        // top/bottom rules instead of being clipped by them.
        'cursor-pointer transition duration-200 hover:z-10 hover:-translate-y-1 hover:shadow-lg',
        card.gridClass,
        card.tone,
        !isWith && 'grayscale-[.65]',
      )}
    >
      {/* Logo only on the "with" side, and never on the quote card. */}
      {isWith && card.kind !== 'quote' ? <AppPill card={card} /> : null}

      {card.kind === 'quote' ? (
        <>
          <p className="max-w-xl pr-8 text-sm font-semibold text-balance lg:text-base">
            {`"${t(`${base}.text`)}"`}
          </p>
          <div className="mt-auto flex items-end justify-between gap-4 pt-2">
            <span className="text-xs opacity-80 lg:text-sm">
              {t(`${base}.author`)}
            </span>
            <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-muted">
              <ArrowRight className="size-5 text-primary" aria-hidden />
            </span>
          </div>
        </>
      ) : (
        <>
          {/* "top" stats sit up here (mini cards); the tall hero anchors its
              number down in the bottom row instead. */}
          {card.statAnchor === 'top' ? (
            <StatBlock
              stat={t(`${base}.stat`)}
              title={t(`${base}.title`)}
              statClass={card.statClass}
              titleClass={card.titleClass}
              className="pr-8"
            />
          ) : null}

          <div className="mt-auto flex items-end justify-between gap-4 pt-2">
            <div className="max-w-xs">
              {card.statAnchor === 'bottom' ? (
                <StatBlock
                  stat={t(`${base}.stat`)}
                  title={t(`${base}.title`)}
                  statClass={card.statClass}
                  titleClass={card.titleClass}
                />
              ) : null}
            </div>
            <BoxedArrow variant={card.boxedArrow} />
          </div>
        </>
      )}

      {/* "Sans CountAct" reuses the same design, greyed out by a veil. */}
      {!isWith ? (
        <div
          className="pointer-events-none absolute inset-0 bg-slate-400/40"
          aria-hidden
        />
      ) : null}
    </Link>
  )
}

/** Brand logo (glyph + wordmark), top-right of every card — the reference's
 * pill. Inverted to white on the blue hero. */
function AppPill({ card }: { card: (typeof COMPARE_CARDS)[number] }) {
  return (
    <img
      src={card.logo}
      alt={card.appName}
      loading="lazy"
      decoding="async"
      className={cn(
        'absolute top-5 right-5 h-6 w-auto max-w-[55%] object-contain object-right lg:top-6 lg:right-6 lg:h-7',
        card.iconClass,
      )}
    />
  )
}

/** Bottom-right chevron: boxed (light/vivid) or bare when unset. */
function BoxedArrow({ variant }: { variant?: 'light' | 'vivid' }) {
  if (!variant) return <ArrowRight className="size-5 shrink-0" aria-hidden />
  return (
    <span
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-xl',
        variant === 'vivid' ? 'bg-primary-vivid' : 'bg-secondary',
      )}
    >
      <ArrowRight
        className={cn(
          'size-5',
          variant === 'vivid' ? 'text-white' : 'text-primary',
        )}
        aria-hidden
      />
    </span>
  )
}

function StatBlock({
  stat,
  title,
  statClass,
  titleClass,
  className,
}: {
  stat: string
  title: string
  statClass?: string
  titleClass?: string
  className?: string
}) {
  return (
    <div className={className}>
      <p
        className={cn(
          'font-serif text-4xl font-semibold tracking-tight',
          statClass,
        )}
      >
        {stat}
      </p>
      <p
        className={cn(
          'mt-1.5 max-w-xs text-base font-medium lg:text-lg',
          titleClass,
        )}
      >
        {title}
      </p>
    </div>
  )
}

/** One shared layout rendered once per tab; only content + tone change.
 * Left column: blue hero stat + the quote beneath it. Right column: three
 * stacked mini stats (lg+). */
function CompareBento({ tab }: { tab: CompareTab }) {
  return (
    <div className="grid grid-cols-1 gap-3 lg:min-h-[16rem] lg:grid-cols-5 lg:grid-rows-3 lg:gap-4">
      {COMPARE_CARDS.map((card) => (
        <CompareCard key={card.key} card={card} tab={tab} />
      ))}
    </div>
  )
}

/** Tab pill whose highlight slides between tabs via a shared `layoutId`. */
function TabButton({
  value,
  active,
  greyed,
  icon: Icon,
  label,
}: {
  value: CompareTab
  active: boolean
  greyed?: boolean
  icon: LucideIcon
  label: string
}) {
  return (
    <TabsTrigger
      value={value}
      className="relative rounded-lg px-6 py-2.5 data-[state=inactive]:opacity-60 data-[state=active]:bg-transparent data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
    >
      {active ? (
        <motion.span
          layoutId="compare-tab-pill"
          className={cn(
            'absolute inset-0 rounded-lg border border-[#0211D9] bg-linear-to-r/srgb from-[#0211D9] to-[#2736F8]',
            greyed && 'grayscale-[.65]',
          )}
          transition={springPill}
        />
      ) : null}
      <span className="relative z-10 flex w-full items-center justify-center gap-2">
        <Icon className="size-4" aria-hidden />
        {label}
      </span>
    </TabsTrigger>
  )
}

export function CompareSection() {
  const t = useTranslations('home.compare')
  const [tab, setTab] = useState<CompareTab>('without')

  // Auto-reveal the "after": start on "without" and flip to "with" once the
  // bento scrolls into view. Leaving the viewport only rewinds when the bento
  // sits *below* it — i.e. the reader scrolled back up above the section. Once
  // it has been read past, the "after" state sticks.
  const bentoRef = useRef<HTMLDivElement>(null)
  const inView = useInView(bentoRef, { amount: 0.12 })
  useEffect(() => {
    if (inView) {
      setTab('with')
      return
    }
    const rect = bentoRef.current?.getBoundingClientRect()
    if (rect && rect.top > 0) setTab('without')
  }, [inView])

  return (
    <section className="pb-16 lg:pb-24">
      <RevealGroup className="mx-auto max-w-7xl px-6 lg:px-8">
        <RevealItem className="mx-auto max-w-3xl text-center">
          <h2 className="font-serif text-4xl font-semibold tracking-tight sm:text-5xl">
            <span className="text-accent-foreground">{t('titleLead')}</span>{' '}
            <HighlightText>{t('titleHighlight')}</HighlightText>
          </h2>
          <p className="mt-3 font-mono text-sm text-secondary-foreground">
            {t('subtitle')}
          </p>
        </RevealItem>

        <RevealItem className="mt-8">
          <Tabs
            value={tab}
            onValueChange={(v) => setTab(v as CompareTab)}
            className="items-center"
          >
            {/* Full-width two-up on mobile, centered rounded box on desktop. */}
            <TabsList className="grid h-auto w-full grid-cols-2 rounded-lg border border-border bg-transparent p-0 lg:inline-flex lg:w-auto">
              <TabButton
                value="without"
                active={tab === 'without'}
                greyed
                icon={X}
                label={t('tabs.without')}
              />
              <TabButton
                value="with"
                active={tab === 'with'}
                icon={Check}
                label={t('tabs.with')}
              />
            </TabsList>
            {/* The bento (the real panel) is rendered full-bleed below, outside
                Tabs. These stubs give each trigger's aria-controls a valid
                target so the tablist stays well-formed. */}
            <TabsContent value="without" />
            <TabsContent value="with" />
          </Tabs>
        </RevealItem>
      </RevealGroup>

      {/* Full-bleed band: muted top/bottom rules reach the screen edges while
          the cards stay constrained. AnimatePresence fades the bento on swap. */}
      {/* No Reveal here: the bento already has its entrance — the `useInView`
          above flipping without→with, plus the AnimatePresence. A Reveal on top
          would mean two competing entrance animations on the same block, in the
          one section where movement already is the message. Its useInView is
          deliberately not `once`, unlike the reveals: it replays when the reader
          scrolls back above the section. */}
      <div ref={bentoRef} className="mt-8 border-y border-border">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={tab}
              variants={contentVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.22, ease: 'easeOut' }}
            >
              <CompareBento tab={tab} />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  )
}
